import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Helpers } from '~commonInfra/utils/Helpers';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';

import { AppState } from '../../../bootstrap/redux/state/AppState';
import { SpaceInfoItemType } from '../../models/ContentType';
import { ISpaceItemDetailInfo } from '../../models/ISpaceItemDetailInfo';
import { ISpaceTypeInfo } from '../../models/resourceInfoParts';
import { AvailableActions } from '../../state/AvailableActionsForEntityState';
import { IEntityAction } from '../ActionsMenu/EntityActions/IEntityAction';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { SpaceHeaderDispatchProps } from './SpaceHeader';
import { SpaceHeaderStyles } from './SpaceHeaderStyles';


export type SpotlightAction<C extends SpaceInfoItemType> = IEntityAction<ISpaceTypeInfo<C>, any>;

export interface SpotlightActionButtonsProps<C extends SpaceInfoItemType> {
  readonly actions: SpotlightAction<C>[];
  readonly item: ISpaceItemDetailInfo<C>;
  readonly availableActions: AvailableActions;
  readonly dispatchSpotlightAction: SpaceHeaderDispatchProps['dispatchSpotlightAction'];
}


/**
 * The way the entity actions are configured, it's not safe to use the state because we're not
 * subscribing to the store for updates. So if the store were to update after the header renders,
 * the spotlight actions wouldn't update. This is okay as long as the particular actions'
 * isVisible/isClickable don't rely on the state; if we try to use one that does, we'll get a
 * NPE at runtime.
 *
 * If we ever do need to use spotlight actions that depend on the state, we can accomplish this
 * by moving the makeButton function into SpaceHeader's mapStateToProps, as we do in the
 * ConnectedActionsMenuPanel.
 */
const NullAppState = null as AppState;


function throwIfPropsAreInvalid<C extends SpaceInfoItemType>(props: SpotlightActionButtonsProps<C>): void {
  const allPropsDefined = _.every(props, Helpers.isDefined);
  if (!allPropsDefined) {
    const missingProps = _(props).omit(Helpers.isDefined);
    const missingPropsString = _(missingProps).keys().join(', ');
    throw new Error(`SpotlightActionButtons component is missing props: ${missingPropsString}`);
  }
}


export function SpotlightActionButtons<C extends SpaceInfoItemType>(props: SpotlightActionButtonsProps<C>): JSX.Element {
  throwIfPropsAreInvalid(props);

  const targetItems = [props.item as ISpaceTypeInfo<C>];

  function shouldShowAction(action: SpotlightAction<C>): boolean {
    return action.isVisible(targetItems, NullAppState) &&
      action.isClickable(props.availableActions, targetItems, NullAppState);
  }

  function makeButton(action: SpotlightAction<C>) {
    function doAction() {
      const reduxAction = action.createReduxAction(targetItems);
      props.dispatchSpotlightAction(reduxAction);
    }

    return <ButtonWidget key={action.name}
      buttonType={ButtonType.Outline}
      children={action.translateName()}
      handleClick={doAction}
      testId={null}
      style={{ fontWeight: 'normal', }}
    />;
  }

  const actionsToShow = props.actions.filter(shouldShowAction);
  if (actionsToShow.length < 1) {
    return null;
  }

  const classNames = [
    ExplorerStyles.ToolbarButtonGroup,
    !FeatureFlags.compactHeaderEnabled ? SpaceHeaderStyles.SpotlightActionButtonsRow : null,
  ].join(' ');

  return (
    <div className={classNames}>
      {actionsToShow.map(makeButton)}
    </div>
  );
}
